<template>
  <!-- Loader overlay -->
  <div v-if="isLoader" class="d-flex justify-content-center align-items-center" style="min-height:220px">
    <div class="spinner-border text-danger" role="status" style="width:5rem;height:5rem">
      <span class="visually-hidden">Chargement...</span>
    </div>
  </div>

  <div
    class="card mb-25 border-0 rounded-0 bg-white letter-spacing"
    :style="isLoader ? { 'pointer-events': 'none', opacity: '0.08' } : {}"
  >
    <!-- ── Header ── -->
    <div class="p-15 p-md-20 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
      

      <div class="d-flex align-items-center gap-2 flex-wrap">
        <!-- Search -->
        <div class="position-relative">
          <i class="flaticon-search-interface-symbol position-absolute top-50 translate-middle-y ms-2 text-muted" style="left:4px;font-size:13px"></i>
          <input
            type="text"
            v-model="searchQuery"
            class="form-control form-control-sm rounded-1 ps-4"
            style="min-width:220px"
            placeholder="Rechercher un numéro..."
          />
          <button v-if="searchQuery" class="btn btn-sm position-absolute top-50 translate-middle-y end-0 me-1 p-0 border-0 bg-transparent text-muted" @click="searchQuery = ''">
            <i class="ri-close-line"></i>
          </button>
        </div>

        <!-- Add button -->
        <button
          v-if="auth.can('BLACK_LISTE')"
          class="btn btn-danger btn-sm fw-medium d-flex align-items-center gap-1"
          @click="openCreateModal"
        >
          <i class="flaticon-plus fs-12"></i> Ajouter un numéro
        </button>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="card-body p-0">
      <div class="table-scroll-outer">
        <div class="table-scroll-mobile">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-20 text-uppercase fs-12 text-muted fw-semibold">#</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Téléphone</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Remarque</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Ajouté le</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Ajouté par</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold text-end pe-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedRows.length === 0">
              <td colspan="6" class="text-center py-40 text-muted">
                <i class="flaticon-cancel fs-40 d-block mb-10 opacity-25"></i>
                Aucun numéro trouvé
              </td>
            </tr>
            <tr
              v-for="(row, i) in paginatedRows"
              :key="row.id"
              class="border-top table-row-hover"
            >
              <td class="ps-20 text-muted fs-13">{{ (currentPage - 1) * perPage + i + 1 }}</td>

              <td>
                <span >
                  <i class="flaticon-telephone me-1"></i>{{ row.phone }}
                </span>
              </td>

              <td class="fs-13 text-muted" style="max-width:220px">
                <span v-if="row.desc" class="text-truncate d-inline-block" style="max-width:200px" :title="row.desc">
                  {{ row.desc }}
                </span>
                <span v-else class="text-muted fst-italic">—</span>
              </td>

              <td class="fs-13 text-muted">{{ getDate(row.created_at) }}</td>

              <td>
                <span class="fw-medium fs-13">{{ row.name ?? '—' }}</span>
              </td>

              <td class="text-end pe-20">
                <div class="d-flex align-items-center justify-content-end gap-1" v-if="auth.can('BLACK_LISTE')">
                  <button
                    class="action-btn action-btn--info"
                    title="Vérifier le numéro"
                    @click="checkPhone(row.id, row.phone)"
                  >
                    <i class="flaticon-check"></i>
                  </button>
                  <button
                    class="action-btn action-btn--danger"
                    title="Supprimer"
                    @click="deleteRow(row.id, row.phone)"
                  >
                    <i class="flaticon-delete"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>

      <!-- ── Pagination ── -->
      <div v-if="filteredRows.length > perPage" class="d-flex align-items-center justify-content-between px-20 py-12 border-top flex-wrap gap-2">
        <span class="text-muted fs-13">
          Affichage {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, filteredRows.length) }} sur {{ filteredRows.length }}
        </span>
        <div class="d-flex gap-1">
          <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 1" @click="currentPage--">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <button
            v-for="p in totalPages" :key="p"
            class="btn btn-sm"
            :class="p === currentPage ? 'btn-danger' : 'btn-outline-secondary'"
            @click="currentPage = p"
          >{{ p }}</button>
          <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage === totalPages" @click="currentPage++">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ── Modal ── -->
  <div class="modal fade" id="createnewRowModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom bg-danger bg-opacity-10">
          <h5 class="modal-title fw-bold text-danger">
            <i class="flaticon-cancel me-2"></i>
            {{ isEditing ? "Modifier le numéro" : "Ajouter un numéro" }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-20">
          <form @submit.prevent="submitRow">
            <div class="form-group mb-15">
              <label class="form-label fw-semibold fs-14">
                Numéro de téléphone <span class="text-danger">*</span>
              </label>
              <div class="input-group">
                <span class="input-group-text"><i class="ri-phone-line"></i></span>
                <input
                  type="text"
                  v-model="newRow.phone"
                  class="form-control"
                  placeholder="Ex: 0612345678"
                  required
                />
              </div>
            </div>

            <div class="form-group mb-5">
              <label class="form-label fw-semibold fs-14">Remarque</label>
              <textarea
                class="form-control shadow-none fs-14 text-black"
                v-model="newRow.desc"
                rows="3"
                placeholder="Motif du blocage (optionnel)..."
              ></textarea>
            </div>
          </form>
        </div>

        <div class="modal-footer border-top">
          <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">
            Annuler
          </button>
          <button type="button" @click="submitRow" class="btn btn-danger btn-sm fw-medium" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEditing ? "Mettre à jour" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import moment from "moment";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useAppToast } from "@/composables/useAppToast";

export default {
  name: "BlackListeContent",
  setup() {
    const { showToast } = useAppToast();
    const auth = useAuthStore();
    const user = auth.user;

    const rows = ref([]);
    const isLoader = ref(false);
    const isSaving = ref(false);
    const isEditing = ref(false);
    const searchQuery = ref("");
    const currentPage = ref(1);
    const perPage = 10;

    const newRow = reactive({ id: null, phone: "", desc: "" });

    // ── Filtered + paginated ──
    const filteredRows = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      if (!q) return rows.value;
      return rows.value.filter(
        (r) =>
          r.phone?.toLowerCase().includes(q) ||
          r.desc?.toLowerCase().includes(q) ||
          r.name?.toLowerCase().includes(q)
      );
    });

    const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / perPage)));

    const paginatedRows = computed(() => {
      const start = (currentPage.value - 1) * perPage;
      return filteredRows.value.slice(start, start + perPage);
    });

    watch(searchQuery, () => { currentPage.value = 1; });

    // ── Data ──
    async function fetchRows() {
      isLoader.value = true;
      try {
        const { data } = await api.get("/api/auth/Black_List");
        rows.value = data ?? [];
      } catch {
        showToast("Erreur lors du chargement", "error");
      } finally {
        isLoader.value = false;
      }
    }

    onMounted(fetchRows);

    // ── Modal ──
    function resetForm() {
      Object.assign(newRow, { id: null, phone: "", desc: "" });
    }

    function openCreateModal() {
      isEditing.value = false;
      resetForm();
      new Modal(document.getElementById("createnewRowModal")).show();
    }

    // ── Submit ──
    async function submitRow() {
      if (!newRow.phone.trim()) {
        showToast("Le numéro de téléphone est obligatoire", "error");
        return;
      }
      isSaving.value = true;
      try {
        const response = await api.post("/api/auth/Add_To_ListB", {
          phone: newRow.phone,
          desc: newRow.desc,
          user_id: user.id,
        });

        if (response.data === "success") {
          const modalEl = document.getElementById("createnewRowModal");
          (Modal.getInstance(modalEl) || new Modal(modalEl)).hide();
          resetForm();
          fetchRows();
          showToast("Numéro ajouté à la liste noire", "success");
        } else if (response.data === "exist") {
          showToast("Ce numéro est déjà dans la liste noire", "error");
        } else {
          showToast("Erreur : " + (response.data?.message ?? response.data ?? "inconnue"), "error");
        }
      } catch {
        showToast("Une erreur est survenue", "error");
      } finally {
        isSaving.value = false;
      }
    }

    // ── Delete ──
    async function deleteRow(id, phone) {
      const result = await Swal.fire({
        title: "Supprimer ce numéro ?",
        html: `Le numéro <strong>${phone}</strong> sera retiré de la liste noire.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await api.post("/api/auth/remove_Phone", {
          phone_id: id,
          phone,
          user_id: user.id,
        });
        if (data === "success") {
          fetchRows();
          showToast("Numéro supprimé avec succès", "success");
        } else {
          showToast("Erreur lors de la suppression", "error");
        }
      } catch {
        showToast("Une erreur est survenue", "error");
      }
    }

    // ── Check ──
    async function checkPhone(id, phone) {
      try {
        const { data } = await api.post("/api/auth/Check_BLK_Phone", {
          phone,
          phone_id: id,
          user_id: user.id,
        });
        if (data.msg === "exist") {
          Swal.fire({
            title: `Numéro : ${phone}`,
            html: `<span class="text-danger fw-bold fs-5">${data.data}</span> colis retourné(s) par ce numéro`,
            icon: "warning",
            confirmButtonColor: "#dc3545",
            confirmButtonText: "Fermer",
          });
        } else {
          showToast("Aucun colis retourné pour ce numéro", "success");
        }
      } catch {
        showToast("Erreur lors de la vérification", "error");
      }
    }

    function getDate(value) {
      return moment.utc(String(value)).format("DD/MM/YYYY HH:mm");
    }

    return {
      auth,
      rows,
      isLoader,
      isSaving,
      isEditing,
      newRow,
      searchQuery,
      currentPage,
      perPage,
      totalPages,
      filteredRows,
      paginatedRows,
      openCreateModal,
      submitRow,
      deleteRow,
      checkPhone,
      fetchRows,
      getDate,
    };
  },
};
</script>

<style scoped>
.table-row-hover:hover { background: #fff5f5; }

thead th {
  background: #f8f9fa;
  padding: 10px 14px;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}
tbody td { padding: 12px 14px; }

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.12s;
}
.action-btn:hover { opacity: 0.82; transform: scale(1.08); }

.action-btn--info  { background: #e3f2fd; color: #0d6efd; }
.action-btn--danger { background: #fce8e8; color: #dc3545; }
</style>
